import { parsePeopleCsv, ROLE_LABELS, type ImportedEmployee } from '@cfl/shared';
import * as React from 'react';

import { BulkImportDialog } from '@/components/bulk-import-dialog';
import {
  findEmployeeIdsByEmail,
  flattenDepartments,
  useCreateEmployee,
  useDepartments,
  useDesignations,
} from '@/lib/people-api';

/** A parsed row with its department, designation and manager already looked up. */
interface ResolvedEmployee extends ImportedEmployee {
  departmentId: string | null;
  designationId: string | null;
  reportingManagerId: string | null;
  /** What the sheet named that the org does not hold. Empty on a clean row. */
  missing: string[];
}

/** Case- and space-insensitive, so "business development" matches the real title. */
function key(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

/**
 * Bulk onboarding — one row per new joiner, the Onboard employee dialog's fields as
 * columns.
 *
 * CREATES ONLY, like every other import and like the seed. An address already on the
 * team is refused by the API and reported as skipped, by name; nothing about that
 * person is touched. A bulk sheet that updated the rows it matched would be exactly
 * the silent overwrite the seed rule exists to prevent — re-importing last quarter's
 * joiner list would revert every promotion, transfer and reporting-line change HR had
 * made in the app since, and nothing on any screen would look wrong.
 *
 * Department, designation and manager are named, not id'd — nobody types a cuid into a
 * spreadsheet — and are resolved BEFORE anything is created, so an unmatched one is
 * visible in the preview rather than discovered forty rows in. Nothing is auto-created
 * from a name: a department invented from a typo is one nobody notices until the org
 * chart has two of them. The API still validates every row it is handed; this
 * resolution is a courtesy, never the control.
 */
export function PeopleImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateEmployee();
  const { data: departmentTree } = useDepartments();
  const { data: designations } = useDesignations();

  // Both lists come back whole, so an unmatched name here really is unmatched.
  const departments = React.useMemo(
    () => flattenDepartments(departmentTree ?? []).map((d) => ({ id: d.id, label: key(d.label) })),
    [departmentTree],
  );
  const titles = React.useMemo(
    () => new Map((designations ?? []).map((d) => [key(d.title), d.id])),
    [designations],
  );

  /**
   * A department may be written as the leaf ("Development") or as the full path the
   * dropdown shows ("Engineering > Development"). Both land on the same row.
   */
  function findDepartment(name: string): string | undefined {
    const wanted = key(name);
    return departments.find((d) => d.label === wanted || d.label.endsWith(`>${wanted}`))?.id;
  }

  async function parse(csv: string) {
    const { employees, errors } = parsePeopleCsv(csv);
    const managers = await findEmployeeIdsByEmail(
      employees.map((e) => e.reportingManagerEmail ?? ''),
    );

    const rows = employees.map<ResolvedEmployee>((row) => {
      const missing: string[] = [];

      const departmentId = row.departmentName ? (findDepartment(row.departmentName) ?? null) : null;
      if (row.departmentName && !departmentId) missing.push(`department "${row.departmentName}"`);

      const designationId = row.designationTitle
        ? (titles.get(key(row.designationTitle)) ?? null)
        : null;
      if (row.designationTitle && !designationId) {
        missing.push(`designation "${row.designationTitle}"`);
      }

      const reportingManagerId = row.reportingManagerEmail
        ? (managers.get(row.reportingManagerEmail) ?? null)
        : null;
      if (row.reportingManagerEmail && !reportingManagerId) {
        missing.push(`manager ${row.reportingManagerEmail}`);
      }

      return { ...row, departmentId, designationId, reportingManagerId, missing };
    });

    return { rows, errors };
  }

  return (
    <BulkImportDialog<ResolvedEmployee>
      open={open}
      onOpenChange={onOpenChange}
      title="Bulk onboard employees"
      description="One row per new joiner. Department, designation and reporting manager are matched by name and must already exist — leave a column blank to set it later. Import only creates; anyone already on the team is left exactly as they are."
      templateHref="/people-import-sample.csv"
      noun="employee"
      parse={parse}
      summarise={(row) => {
        const summary = {
          text: ROLE_LABELS[row.role],
          detail: [row.email, row.designationTitle, row.departmentName].filter(Boolean).join(' · '),
        };
        if (row.missing.length === 0) return summary;
        return {
          ...summary,
          blocker: `Not found: ${row.missing.join(', ')}. Create ${
            row.missing.length === 1 ? 'it' : 'them'
          } first, or clear the column and set it later.`,
        };
      }}
      create={(row) =>
        create.mutateAsync({
          email: row.email,
          name: row.name,
          employeeCode: row.employeeCode,
          role: row.role,
          departmentId: row.departmentId,
          designationId: row.designationId,
          reportingManagerId: row.reportingManagerId,
          joinedAt: row.joinedAt,
        })
      }
    />
  );
}
